import React, { createContext, useContext, useEffect, useState } from "react"
import { useAuthProvider } from "./AuthProvider"

const MAX_SCREENSHOTS = 100

type ScreenshotMetadata = {
    fileName: string
	displayStartTime: string
	displayEndTime: string
    timeGenerated: Date
    displayType: string
	isDeidentified: boolean
	mrn: string
	patientIdInternal: number
}

export interface ScreenshotEntry {
	id: string
	imageData: string // base64
	metadata: ScreenshotMetadata
}

interface ScreenshotContextValue {
	screenshots: ScreenshotEntry[]
	store: (imageData: string, metadata: ScreenshotMetadata) => void
	remove: (id: string) => void
	saveToDevice: (screenshot: ScreenshotEntry) => void
}

const ScreenshotContext = createContext<ScreenshotContextValue | null>(null)

export function ScreenshotProvider({ children }: { children: React.ReactNode }) {
	const authProvider = useAuthProvider()
	const [screenshots, setScreenshots] = useState<ScreenshotEntry[]>([])

    const store = (imageData: string, metadata: ScreenshotMetadata) => {
        setScreenshots(prev => {
            const entry: ScreenshotEntry = {
                id: `${Date.now()}`,
                imageData,
                metadata,
            }

			const next = [entry, ...prev]
			return next.length > MAX_SCREENSHOTS ? next.slice(0, next.length-1) : next
		})
    }

	const remove = (id: string) => {
		setScreenshots(prev => prev.filter(s => s.id !== id))
	}

	const saveToDevice = (screenshot: ScreenshotEntry): void => {
		const link = document.createElement("a")
		link.href = screenshot.imageData
		link.setAttribute("download", screenshot.metadata.fileName)
		document.body.appendChild(link)
		link.click()
		link.parentNode?.removeChild(link)
	}

	useEffect(() => {
		// Make sure to remove screenshots on log out
		if (!authProvider.userIsLoggedIn) {
			setScreenshots([])
		}

	}, [authProvider.userIsLoggedIn])

	return (
		<ScreenshotContext.Provider value={{ screenshots, store, remove, saveToDevice }}>
			{children}
		</ScreenshotContext.Provider>
	)
}

export function useScreenshotProvider(): ScreenshotContextValue {
	const ctx = useContext(ScreenshotContext)
	if (!ctx) throw new Error("useScreenshots must be used within a ScreenshotProvider")
	return ctx
}
